import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { useKeyPress } from "ahooks"
import classNames from "classnames"
import { motion } from "motion/react"
import Scroll from "@hg/scroll"

import { useTranslation } from "@/locales"
import { getPlayerSummary, getServerSummary } from "@/services"
import Exit from "../../components/Exit"
import Nav from "../../components/Nav"
import PlayerSummary from "../../components/PlayerSummary"
import ServerSummary from "../../components/ServerSummary"
import SubpageBg from "../../components/SubpageBg"
import TabButton from "../../components/TabButton"
import Toast from "../../components/Toast"
import { ESubPage, GlobalContext, ISubPageProps } from "../../context"

import styles from "./index.module.less"

interface Char {
    id: string
    rarity: number
    name: string
    profession: string
}

export interface IServerSummary {
    endings: Record<string, string>
    activeTool: { id: string; name: string }[]
    relic: { id: string; name: string }[]
    character: Record<string, Char[]>
    wrath: { id: string; name: string }[]
    copper: { id: string; name: string }[]
    ticket: number
    candle: Char[]
    expeditionGuide: Char[]
    costHp: number
    nodePass: number
    fall: number
    rotate: number
    gold: number
    raidian: number
}

export interface IPlayerSummary {
    clearInfo: Record<string, number>
    gold: number
    node: number
    hope: number
    upgrade: number
    candle: number
    expeditionGuide: number
    candleByBattle: number
    expedition: number
    trade: number
    skyBattle: number
    killRgdysm: number
    dyspllKillChar: number
    wrath: {
        total: number
        cnt12: number
    }
    copperGain: Record<string, number>
    maxCopperGain: {
        id: string
        name: string
    }
    copperDraw: Record<string, number>
}

const Content = () => {
    const { t } = useTranslation()
    const Tabs = [
        { key: "server", label: t("server.1.title") },
        { key: "player", label: t("player.1.title") },
    ]
    const [activeTab, setActiveTab] = useState("server")

    const {
        state: { user },
    } = useContext(GlobalContext)

    const [playerSummary, setPlayerSummary] = useState<IPlayerSummary | null>(
        null
    )
    const [serverSummary, setServerSummary] = useState<IServerSummary | null>(
        null
    )
    const fetchServerSummary = useCallback(async () => {
        const res = await getServerSummary<IServerSummary>("rogue_5")
        if (res.code === 0) {
            setServerSummary(res.data)
        } else {
            Toast.message({ content: res.msg })
        }
    }, [])

    const fetchPlayerSummary = useCallback(async () => {
        const res = await getPlayerSummary<IPlayerSummary>("rogue_5")
        if (res.code === 0) {
            setPlayerSummary(res.data)
        } else {
            Toast.message({ content: res.msg })
        }
    }, [])
    useEffect(() => {
        if (user?.uid) {
            fetchServerSummary()
            fetchPlayerSummary()
        }
    }, [fetchPlayerSummary, fetchServerSummary, user?.uid])

    const content = useMemo(() => {
        if (activeTab === "player") {
            return (
                <PlayerSummary
                    data={playerSummary}
                    className={styles.summary}
                />
            )
        }

        return <ServerSummary data={serverSummary} className={styles.summary} />
    }, [activeTab, playerSummary, serverSummary])

    const scrollRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0
        }
    }, [activeTab])

    return (
        <div className={styles.container}>
            <div className={styles.tabs}>
                {Tabs.map((tab) => (
                    <TabButton
                        key={tab.key}
                        className={styles.tab}
                        active={activeTab === tab.key}
                        label={tab.label}
                        onClick={() => setActiveTab(tab.key)}
                    />
                ))}
            </div>
            <div className={styles.content}>
                <div className={styles.wrapper}>
                    <div className={styles.shadow} />
                    <div className={styles.header} />
                    <div className={styles.center} />
                    <div className={styles.border}>
                        <div />
                    </div>
                    <Scroll
                        ref={scrollRef}
                        direction="y"
                        className={styles.scroll}
                        rotateDetection
                    >
                        {content}
                    </Scroll>
                </div>
            </div>
        </div>
    )
}

const PageRecord: React.FC<ISubPageProps> = (props) => {
    const { className, style, onExit } = props

    useKeyPress("esc", () => {
        onExit?.()
    })

    return (
        <motion.div
            className={classNames(styles.page, className)}
            style={style}
            initial={{
                opacity: 0,
                pointerEvents: "none",
            }}
            animate={{
                opacity: 1,
                pointerEvents: "auto",
            }}
            exit={{
                opacity: 0,
                pointerEvents: "none",
            }}
            transition={{
                duration: 0.5,
                ease: "easeInOut",
            }}
        >
            <SubpageBg />
            <Content />
            <Nav page={ESubPage.Record} />
            <Exit onClick={onExit} />
        </motion.div>
    )
}

export default PageRecord
